/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-no-target-blank */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import styles from "./registrationScreen.module.css";
import { ajaxLink } from "../self-assesment/CONSTANTS";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

/** Basic validation rules */
const validators = {
  firstname: (v) => /^[a-zA-Z]{2,}$/.test(v?.trim()),
  lastname: (v) => /^[a-zA-Z]{2,}$/.test(v?.trim()),
  email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v?.trim()),
  phone: (v) => /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(v),
  charLength: (v) => v?.length >= 8,
  hasLetter: (v) => /[a-zA-Z]/.test(v),
  hasNum: (v) => /[0-9]/.test(v),
  hasSpecChar: (v) => /[^a-zA-Z0-9]/.test(v),
};

/** Format a raw phone string to "(xxx) xxx-xxxx" */
const formatPhone = (raw) => raw.replace(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/, "($1) $2-$3");

// ─────────────────────────────────────────────
// StateSelect — inline, self-contained dropdown
// Props:
//   options        : [{ value: string }]
//   value          : string  (controlled)
//   onChange       : (value: string) => void
//   placeholder    : string
//   hasError       : boolean
// ─────────────────────────────────────────────
function StateSelect({ options = [], value, onChange, placeholder, hasError }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Auto-focus search when dropdown opens */
  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open]);

  const toggleOpen = () => {
    setOpen((prev) => {
      if (prev) setSearch(""); // clear search on close
      return !prev;
    });
  };

  const handleSelect = (optVal) => {
    onChange(optVal);
    setOpen(false);
    setSearch("");
  };

  /* Filter options by search string */
  const filtered = options.filter((o) => o.value.toLowerCase().startsWith(search.toLowerCase()));

  return (
    <div ref={wrapperRef} className={`${styles.selWrapper} ${hasError ? styles.selWrapperError : ""}`}>
      {/* Trigger button */}
      <div className={`${styles.selTrigger} ${open ? styles.selTriggerOpen : ""}`} onClick={toggleOpen} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && toggleOpen()}>
        <span className={value ? styles.selValue : styles.selPlaceholder}>{value || placeholder}</span>
        <span className={`${open ? styles.selArrowClose : styles.selArrow}`} />
      </div>

      {/* Dropdown panel */}
      <div className={`${styles.selDropdown} ${open ? styles.selDropdownOpen : ""}`}>
        {/* Search box */}
        <div className={styles.selSearchContainer}>
          <input ref={searchInputRef} type="text" className={styles.selSearch} placeholder="Search state…" value={search} onChange={(e) => setSearch(e.target.value)} onClick={(e) => e.stopPropagation()} />
          <div className={styles.selSearchIcon} />
        </div>

        {/* Options list */}
        <div className={styles.selOptionList}>
          {filtered.length > 0 ? (
            filtered.map((opt) => (
              <div key={opt.value} className={styles.selOption} onClick={() => handleSelect(opt.value)}>
                {opt.value}
              </div>
            ))
          ) : (
            <div className={styles.selNoResults}>No results found.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// AssessmentData Format the way backend expects
// ─────────────────────────────────────────────

function parseAssessmentData(rawData) {
  if (!rawData || typeof rawData !== "object") return null;
  console.log("raw data is : ", rawData);

  const plan = rawData.recoveryPlan || {};
  const exercises = plan.exercises || [];

  const today = new Date().toISOString().split("T")[0];

  const { recoveryPlan, exercises: _exercises, problem_id, sub_problem_id, clinical_protocol_id, rom, duration, rom2, duration2, analyticsId, ...restUserData } = rawData;

  const formatted = {
    program_name: "Week 1 Recovery Plan",
    problem_id: rawData.problem_id || 0,
    sub_problem_id: rawData.sub_problem_id || 0,
    clinical_protocol_id: rawData.clinical_protocol_id || 0,
    rom: rom || rawData.rom || [],
    duration: duration || rawData.duration || [],
    rom2: rom2 || rawData.rom2 || [],
    duration2: duration2 || rawData.duration2 || [],
    analyticsId: analyticsId || rawData?.analyticsId || null,
    tasks: exercises.map((exercise) => ({
      name: exercise.name || "Untitled",
      description: exercise.description || "",
      level: plan.level || "",
      rom: exercise.target_rom_max || 0,
      hold: exercise?.hold,
      duration: exercise.hold_in_seconds || 0,
      reps: exercise.reps || 4,
      sets: exercise.sets || 1,
      task_type: "exercise",
      category_type: 1,
      suggested_date: today,
      question_id: exercise.question_id || 0,
      activity_id: exercise.activity_id || exercise.equipment_id || null,
      set_limb_instruction: exercise.set_limb_instruction || {},
      performance_side: exercise.performance_side || "",
      purpose: exercise.purpose || "",
      rom_max: exercise.target_rom_max || 100,
    })),
    userData: restUserData,
  };

  return formatted;
}

// ─────────────────────────────────────────────
// Register — main component
// ─────────────────────────────────────────────
const Register = () => {
  const { patientCode } = useParams();
  const history = useHistory();

  // ── Form field state ──
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [stateSelected, setStateSelected] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChecked, setIsChecked] = useState(false);

  // ── Validation error state ──
  const [errFirstName, setErrFirstName] = useState("");
  const [errLastName, setErrLastName] = useState("");
  const [errEmail, setErrEmail] = useState("");
  const [errPhone, setErrPhone] = useState("");
  const [errState, setErrState] = useState("");
  const [errPassword, setErrPassword] = useState("");
  const [errConfirmPass, setErrConfirmPass] = useState("");

  // ── Password strength flags ──
  const [passValid, setPassValid] = useState({
    charLength: false,
    hasLetter: false,
    hasNum: false,
    hasSpecChar: false,
    allValid: false,
  });

  // ── UI state ──
  const [passVisible, setPassVisible] = useState(false);
  const [confirmPassVisible, setConfirmPassVisible] = useState(false);
  const [validToProceed, setValidToProceed] = useState(false);

  // ── State dropdown data ──
  const [usStateList, setUsStateList] = useState([]);
  const [canadaStateList, setCanadaStateList] = useState([]);

  // ── Pre-filled company data (from localStorage or patient code) ──
  const [userCompanyData, setUserCompanyData] = useState(() => {
    const stored = localStorage.getItem("userCompanyData");
    return stored ? JSON.parse(stored)[0] : null;
  });

  // ── Session / analytics ──
  const [sessionId, setSessionId] = useState(null);

  const emailRef = useRef(null);

  // ─── Effects ────────────────────────────────

  /* Decode assessment payload from ?data= param */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encodedData = params.get("data");
    if (!encodedData) return;

    fetch(ajaxLink + "/user/register_data", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code: encodedData }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.responseCode === 200) {
          const decoded = atob(res.responseData);
          const parsed = JSON.parse(decoded);
          setSessionId(parsed?.analyticsId || null);
          localStorage.setItem("assessmentData", JSON.stringify(parsed));
          localStorage.setItem("fromSite", "https://" + (params.get("fromsite") || "www.startfreept.com"));
        }
      })
      .catch((err) => console.error("Failed to decode assessment data:", err));
  }, []);

  /* Hide sidebar / mark as logged-out */
  useEffect(() => {
    window.onbeforeunload = null;
  }, []);

  /* Pre-fill from userCompanyData (localStorage) */
  useEffect(() => {
    if (!userCompanyData) return;
    setFirstName(userCompanyData.first_name || "");
    setLastName(userCompanyData.last_name || "");
    setEmail(userCompanyData.email || "");
    setPhone(userCompanyData.phone || "");
  }, [userCompanyData]);

  /* Store business_id in localStorage */
  useEffect(() => {
    if (document.location.pathname.includes("register-practice")) return;
    const businessId = document.location.search.slice(1);
    localStorage.setItem("business_register", parseInt(businessId) || 0);
  }, []);

  /* Fetch state/province list — retries at 3s and 10s for slow networks */
  const fetchStateList = useCallback(() => {
    if (usStateList.length > 0) return; // already loaded

    fetch(ajaxLink + "/cloud_therapist/getStateList", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.responseData) return;
        const us = res.responseData.filter((s) => s.country === "United States");
        const ca = res.responseData.filter((s) => s.country !== "United States");
        setUsStateList(us);
        setCanadaStateList(ca);
      })
      .catch((err) => console.error("Failed to fetch state list:", err));
  }, [usStateList.length]);

  useEffect(() => {
    fetchStateList();
    const t1 = setTimeout(fetchStateList, 3000);
    const t2 = setTimeout(fetchStateList, 10000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  /* Check patient code from URL param */
  useEffect(() => {
    if (!patientCode) return;

    fetch(ajaxLink + "/user/checkForPatientCode", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ patient_code: patientCode }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.responseCode !== 200 || !res.responseData) return;
        const data = res.responseData[0];

        localStorage.setItem("userCompanyData", JSON.stringify(res.responseData));
        localStorage.setItem("userMainTherapist", JSON.stringify(data.therapist_id));
        localStorage.setItem("company_id", data.company_id);
        localStorage.setItem("business_register", data.business_id || 0);
        setUserCompanyData(data);
      })
      .catch((err) => {
        err
          .json()
          .then((errorData) => {
            if (errorData.errorData === "no user found") {
              localStorage.removeItem("userCompanyData");
            }
            if (errorData.errorData === "user already registered") {
              localStorage.removeItem("userCompanyData");
            }
          })
          .catch(() => {
            console.error("Failed to parse error response");
          });
      });
  }, [patientCode]);

  // ─── Validation helpers ──────────────────────

  /* Validate & set first name */
  const handleFirstName = (v) => {
    const val = v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
    setFirstName(val);
    setErrFirstName(validators.firstname(val) ? "" : "Please enter a valid name.");
  };

  /* Validate & set last name */
  const handleLastName = (v) => {
    const val = v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
    setLastName(val);
    setErrLastName(validators.lastname(val) ? "" : "Please enter a valid last name.");
  };

  /* Validate & set email */
  const handleEmail = (v) => {
    setEmail(v);
    setErrEmail(validators.email(v) ? "" : "Please enter a valid email address.");
  };

  /* Validate & set phone */
  const handlePhone = (v) => {
    if (validators.phone(v)) {
      setPhone(formatPhone(v));
      setErrPhone("");
    } else {
      setPhone(v);
      setErrPhone("Please enter a valid US mobile number.");
    }
  };

  /* Validate password + update strength indicators */
  const handlePassword = (v) => {
    setPassword(v);
    setErrPassword("");

    const updated = {
      charLength: validators.charLength(v),
      hasLetter: validators.hasLetter(v),
      hasNum: validators.hasNum(v),
      hasSpecChar: validators.hasSpecChar(v),
    };
    updated.allValid = Object.values(updated).every(Boolean);
    setPassValid(updated);

    /* Also re-check confirm password match */
    if (confirmPassword) {
      setErrConfirmPass(confirmPassword !== v ? "Passwords don't match." : "");
    }
  };

  /* Validate confirm password */
  const handleConfirmPass = (v) => {
    setConfirmPassword(v);
    setErrConfirmPass(v !== password ? "Passwords don't match." : "");
  };

  /* Show all errors at once when user clicks submit while invalid */
  const triggerAllErrors = () => {
    handleFirstName(firstName);
    if (errEmail !== "This email is already taken, try login instead.") handleEmail(email);
    handlePhone(phone);
    if (!stateSelected) setErrState("Please select a state from the list.");
    if (!password) setErrPassword("Please enter a valid password.");
    if (!confirmPassword) setErrConfirmPass("Please confirm your password.");
  };

  /* Re-compute overall form validity on every relevant change */
  useEffect(() => {
    const nameOk = firstName.length > 1 && !errFirstName;
    const lastOk = lastName.length > 1 && !errLastName;
    const emailOk = email.length > 0 && !errEmail;
    const stateOk = stateSelected.length > 0;
    const phoneOk = phone.length > 0 && !errPhone;
    const passOk = passValid.allValid;
    const confOk = confirmPassword.length > 0 && !errConfirmPass;
    const termsOk = isChecked;

    setValidToProceed(nameOk && lastOk && emailOk && stateOk && phoneOk && passOk && confOk && termsOk);
  }, [firstName, errFirstName, lastName, errLastName, email, errEmail, stateSelected, phone, errPhone, passValid, confirmPassword, errConfirmPass, isChecked]);

  // normalizing the assessment data
  const assessmentData = JSON.parse(localStorage.getItem("assessmentData"));
  const backendData = parseAssessmentData(assessmentData);

  // ─── Submit ──────────────────────────────────

  const handleSubmit = () => {
    /* Normalize birthday format if present */
    let birthday = "";
    if (userCompanyData?.birthday) {
      birthday = userCompanyData.birthday.includes("/") ? userCompanyData.birthday.split("/").reduce((_, __, i, a) => `${a[2]}-${a[0]}-${a[1]}`) : userCompanyData.birthday;
    }

    fetch(ajaxLink + "/user/register", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        birthday,
        email: email.trim().toLowerCase(),
        city: "city",
        country: "United States",
        state: stateSelected,
        phone,
        password: password.trim(),
        session_id: backendData?.analyticsId || sessionId,
        company_id: localStorage.getItem("company_id") || 0,
        business_id: localStorage.getItem("business_register") || 0,
        newMemberRegister: true,
        company_doctor_id: userCompanyData?.company_doctor_id || 0,
        patient_code: patientCode || "",
        assessmentData: backendData || null,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.responseCode === 200) {
          localStorage.setItem("userData", JSON.stringify(res.responseData));
          localStorage.setItem("isNew", "true");
          history.push("/verify-otp");
        }
        if (res.responseCode === 400 && res.errorData) {
          if (res.errorData.emailErr) {
            setErrEmail(res.errorData.emailErr);
            setValidToProceed(false);
            emailRef.current.focus();
            emailRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
          return;
        }
        console.error("Unhandled response:", res);
      })
      .catch((err) => {
        console.error("Network error:", err);
      });
  };
  // ─── Render ──────────────────────────────────

  /* Determine which state list to use (currently always US) */
  const stateOptions = usStateList.map((s) => ({ value: s.value }));

  return (
    <div className={styles.regPageBg}>
      {/* Fixed logo */}
      <div className={styles.regLogoHeader} />

      {/* Decorative phone image */}
      <div className={styles.regPhoneImg} />

      {/* ── White content panel ── */}
      <div className={styles.regContentPanel}>
        {/* "Already have an account?" — desktop top-right */}
        <div className={styles.regAlreadyHaveTop}>
          Already have an account? <Link to="/login">Sign in</Link>
        </div>

        {/* ── Form card ── */}
        <div className={styles.regCard}>
          {/* Title */}
          <div className={styles.regTitle}>{"Sign Up to Continue Your Recovery Journey"}</div>

          {/* Subtitle */}
          <div className={styles.regSubtitle}>{"Unlock your first week of guided recovery with AI-powered exercises, progress tracking, reminders, and more. Let's get started!"}</div>

          {/* ── Fields ── */}
          <div className={styles.regFieldsWrapper}>
            {/* Patient / Name */}
            <div className={styles.regFieldLabel}>{"Name"}</div>

            {/* First + Last name row */}
            <div className={styles.regNameRow}>
              <input type="text" tabIndex={1} placeholder="First Name" className={`${styles.regInput} ${errFirstName ? styles.regInputError : ""}`} value={firstName} onChange={(e) => handleFirstName(e.target.value)} disabled={!!userCompanyData?.first_name} />
              <input type="text" tabIndex={2} placeholder="Last Name" className={`${styles.regInput} ${errLastName ? styles.regInputError : ""}`} value={lastName} onChange={(e) => handleLastName(e.target.value)} disabled={!!userCompanyData?.last_name} />
            </div>

            {/* Name errors */}
            {(errFirstName || errLastName) && <div className={styles.regErrorMsg}>{errFirstName || errLastName}</div>}

            {/* Email */}
            <div className={styles.regFieldLabel}>Email Address</div>
            <div className={styles.regRelative}>
              <input type="email" tabIndex={3} placeholder="john@email.com" ref={emailRef} className={`${styles.regInput} ${errEmail ? styles.regInputError : ""}`} value={email} onChange={(e) => handleEmail(e.target.value)} disabled={!!userCompanyData?.email} />
              {errEmail && <div className={styles.regErrorMsg}>{errEmail}</div>}
            </div>

            {/* State */}
            <div className={styles.regFieldLabel} style={{ width: "100%" }}>
              State
            </div>
            <StateSelect
              options={stateOptions}
              value={stateSelected}
              onChange={(val) => {
                setStateSelected(val);
                setErrState("");
              }}
              placeholder="Select State"
              hasError={!stateSelected && !!errState}
            />
            {errState && <div className={styles.regErrorMsg}>{errState}</div>}

            {/* Phone */}
            <div className={styles.regFieldLabel}>Mobile Number</div>
            <div className={styles.regRelative}>
              <input type="text" tabIndex={5} placeholder="e.g. 202-333-2222" className={`${styles.regInput} ${errPhone ? styles.regInputError : ""}`} value={phone} onChange={(e) => handlePhone(e.target.value)} disabled={!!userCompanyData?.phone} />
              {errPhone && <div className={styles.regErrorMsg}>{errPhone}</div>}
            </div>
            <div className={styles.regHelperText}>You will use your mobile number to sign into the app and receive texts. Standard rates apply.</div>

            {/* Password */}
            <div className={styles.regFieldLabel}>Password</div>
            <div className={styles.regRelative}>
              <input type={passVisible ? "text" : "password"} tabIndex={6} className={`${styles.regInput} ${errPassword ? styles.regInputError : ""}`} onChange={(e) => handlePassword(e.target.value)} onCopy={(e) => e.preventDefault()} onPaste={(e) => e.preventDefault()} />
              {/* Eye toggle */}
              <span className={styles.regEyeBtn} onClick={() => setPassVisible((v) => !v)}>
                {!passVisible && <span className={styles.regEyeSlash} />}
              </span>
            </div>
            {errPassword && <div className={styles.regErrorMsg}>{errPassword}</div>}

            {/* Password strength indicators */}
            {password.length > 0 && !passValid.allValid && (
              <div className={styles.regPassTickers}>
                {[
                  { key: "charLength", label: "Must be at least 8 characters" },
                  { key: "hasLetter", label: "Minimum 1 Letter" },
                  { key: "hasNum", label: "Minimum 1 Number" },
                  { key: "hasSpecChar", label: "Minimum 1 Special character" },
                ].map(({ key, label }) => (
                  <div key={key} className={styles.regPassCheck}>
                    <span className={`${styles.regPassDot} ${passValid[key] ? styles.regPassDotValid : ""}`} />
                    {label}
                  </div>
                ))}
              </div>
            )}

            {/* Confirm Password */}
            <div className={styles.regFieldLabel}>Confirm Password</div>
            <div className={styles.regRelative}>
              <input type={confirmPassVisible ? "text" : "password"} tabIndex={7} className={`${styles.regInput} ${errConfirmPass ? styles.regInputError : ""}`} onChange={(e) => handleConfirmPass(e.target.value)} onCopy={(e) => e.preventDefault()} onPaste={(e) => e.preventDefault()} />
              <span className={styles.regEyeBtnConfirm} onClick={() => setConfirmPassVisible((v) => !v)}>
                {!confirmPassVisible && <span className={styles.regEyeSlashConfirm} />}
              </span>
            </div>
            {errConfirmPass && <div className={styles.regErrorMsg}>{errConfirmPass}</div>}

            {/* Terms & conditions */}
            <div className={styles.regTermsRow}>
              <div className={`${styles.regCheckbox} ${isChecked ? styles.regCheckboxChecked : ""}`} onClick={() => setIsChecked((c) => !c)} />
              <div className={styles.regTermsText}>
                I have read and agree to
                <a href="https://www.virdiohealth.com/terms" target="_blank" rel="noreferrer">
                  Virdio Health's Terms of Service
                </a>
                ,{" "}
                <a href="https://www.virdiohealth.com/privacy" target="_blank" rel="noreferrer">
                  Privacy Notice
                </a>
                ,{" "}
                <a href="https://www.virdiohealth.com/hipaa-notice" target="_blank" rel="noreferrer">
                  HIPAA Notice
                </a>
                .
              </div>
            </div>
          </div>

          {/* Submit button */}
          <div className={styles.regBottomPad}>
            <button className={`${styles.regSubmitBtn} ${!validToProceed ? styles.regSubmitBtnDisabled : ""}`} onClick={validToProceed ? handleSubmit : triggerAllErrors} tabIndex={8}>
              Get Started
            </button>
          </div>

          {/* "Already have an account?" — mobile bottom */}
          <div className={styles.regAlreadyHaveBottom}>
            Already have an account? <Link to="/">Sign in</Link>
          </div>
        </div>
        {/* /regCard */}
      </div>
      {/* /regContentPanel */}
    </div>
  );
};

export default Register;
